// @ts-nocheck
import { customerInvoiceMissing } from "@/lib/customer-invoice";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useForm, useStore } from "@tanstack/react-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import {
  customerFormSchema,
  type CustomerFormData,
  generateCustomerCode,
  lookupBusinessByTaxCode,
  validatePhoneNumber,
} from "../../../shared/schemas/customer.schema";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Save,
  AlertTriangle,
  Camera,
  Trash2,
  Search,
  Sparkles,
  Building2,
  UserCheck,
  CreditCard,
  Truck,
  Palette,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { focusFirstError } from "@/lib/form/formUtils";
import { ComboboxFK } from "@/components/shared/ComboboxFK";
import { useUserLookup } from "@/hooks/useFKLookup";
import { NumericInput } from "@/components/shared/NumericInput";
import { TanstackFieldWrapper, TanstackFormControl } from "@/components/ui/tanstack-form";
import { ComboboxConfigMaster } from "@/components/shared/ComboboxConfigMaster";
import { uploadFile } from "@/lib/uploadFile";
import { validateUploadFile, MAX_AVATAR_LOGO_SIZE, extractErrorMessage } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth.store";
import { useDebounce } from "@/hooks/useDebounce";

function normalize(s: string): string {
  if (!s) return "";
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .replace(/\s+/g, " ")
    .trim();
}


const QUICK_CREDIT_LIMITS = [
  { label: "20M", value: 20_000_000 },
  { label: "50M", value: 50_000_000 },
  { label: "100M", value: 100_000_000 },
  { label: "200M", value: 200_000_000 },
  { label: "500M", value: 500_000_000 },
  { label: "1 Tỷ", value: 1_000_000_000 },
];

export interface CustomerFormProps {
  customerId?: string;
  defaultValues?: Partial<CustomerFormData>;
  onSubmit?: (data: CustomerFormData) => Promise<void | any>;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitLabel?: string;
  isModal?: boolean;
  modalHeader?: React.ReactNode;
  /** Mở từ ô chọn khách hàng (báo giá, đơn hàng…): trùng MST / mã thì cho chọn luôn khách hàng đã có */
  onUseExisting?: (customer: any) => void;
}

export function CustomerForm(props?: CustomerFormProps) {
  const routeParams = (useParams({ strict: false }) as any) || {};
  const customerId = props?.customerId || routeParams.customerId;
  const isEdit = !!customerId && customerId !== "new";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isModal = props?.isModal ?? false;
  const currentUser = useAuthStore((s) => s.user);

  const [isLookingUpTax, setIsLookingUpTax] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const lastAutoCustomerCode = useRef("");

  const { data: initialData, isLoading } = useQuery({
    queryKey: ["customer", customerId],
    queryFn: () => trpcClient.customers.getById.query({ id: customerId }),
    enabled: isEdit,
  });


  const salesLookup = useUserLookup();

  const createMutation = useMutation({
    mutationFn: (data: CustomerFormData) => trpcClient.customers.create.mutate(data),
    onSuccess: () => {
      toast.success("Đã tạo mới khách hàng thành công");
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["customer-lookup"] });
      navigate({ to: "/customers" });
    },
    onError: (err: any) => toast.error("Lỗi tạo khách hàng: " + err.message),
  });

  const updateMutation = useMutation({
    mutationFn: (data: CustomerFormData) =>
      trpcClient.customers.update.mutate({ id: customerId, data }),
    onSuccess: () => {
      toast.success("Đã cập nhật thông tin khách hàng");
      queryClient.invalidateQueries({ queryKey: ["customers"] });
      queryClient.invalidateQueries({ queryKey: ["customer", customerId] });
      queryClient.invalidateQueries({ queryKey: ["customer-lookup"] });
      navigate({ to: "/customers" });
    },
    onError: (err: any) => toast.error("Lỗi cập nhật: " + err.message),
  });

  const defaultSalesId =
    props?.defaultValues?.salesUserId || (currentUser?.id ? currentUser.id : null);

  const form = useForm({
    defaultValues: {
      code: props?.defaultValues?.code ?? "",
      name: props?.defaultValues?.name ?? "",
      shortName: props?.defaultValues?.shortName ?? "",
      type: props?.defaultValues?.type ?? "COMPANY",
      group: props?.defaultValues?.group ?? "",
      representative: props?.defaultValues?.representative ?? "",
      position: props?.defaultValues?.position ?? "Giám đốc",
      taxCode: props?.defaultValues?.taxCode ?? "",
      phone: props?.defaultValues?.phone ?? "",
      mobile: props?.defaultValues?.mobile ?? "",
      email: props?.defaultValues?.email ?? "",
      website: props?.defaultValues?.website ?? "",
      fanpage: props?.defaultValues?.fanpage ?? "",
      logo: props?.defaultValues?.logo ?? "",
      address: props?.defaultValues?.address ?? "",
      deliveryAddress: props?.defaultValues?.deliveryAddress ?? "",
      contactPerson: props?.defaultValues?.contactPerson ?? "",
      deliveryMethod: props?.defaultValues?.deliveryMethod ?? "Giao tới khách (Xe tải APV)",
      transitAddress: props?.defaultValues?.transitAddress ?? "",
      paymentTerms: props?.defaultValues?.paymentTerms ?? "Gối đầu 30 ngày",
      colorTolerance: props?.defaultValues?.colorTolerance ?? "Delta E ≤ 2.0 (Chuẩn cao cấp)",
      tolerancePercent: Number(props?.defaultValues?.tolerancePercent ?? 1.0),
      bankAccountNumber: props?.defaultValues?.bankAccountNumber ?? "",
      bankAccountHolder: props?.defaultValues?.bankAccountHolder ?? "",
      bankName: props?.defaultValues?.bankName ?? "",
      initialDebt: 0,
      creditLimit: Number(props?.defaultValues?.creditLimit ?? 0),
      salesUserId: defaultSalesId,
      tags: props?.defaultValues?.tags ?? "Mới tiếp cận",
      notes: props?.defaultValues?.notes ?? "",
      status: props?.defaultValues?.status ?? "ACTIVE",
    } as CustomerFormData,
    validators: {
      onChange: customerFormSchema,
    },
    onSubmit: async ({ value }) => {
      if (duplicateErrors.length > 0) {
        toast.error("Vui lòng xử lý lỗi trùng lặp dữ liệu trước khi lưu.");
        return;
      }
      if (props?.onSubmit) {
        await props.onSubmit(value);
      } else if (isEdit) {
        await updateMutation.mutateAsync(value);
      } else {
        await createMutation.mutateAsync(value);
      }
    },
  });

  // Sync initialData when editing
  useEffect(() => {
    if (initialData && isEdit) {
      form.setFieldValue("code", initialData.code || "");
      form.setFieldValue("name", initialData.name || "");
      form.setFieldValue("shortName", initialData.shortName || "");
      form.setFieldValue("type", (initialData.type as any) || "COMPANY");
      form.setFieldValue("group", initialData.group || "");
      form.setFieldValue("representative", initialData.representative || "");
      form.setFieldValue("position", initialData.position || "Giám đốc");
      form.setFieldValue("taxCode", initialData.taxCode || "");
      form.setFieldValue("phone", initialData.phone || "");
      form.setFieldValue("mobile", initialData.mobile || "");
      form.setFieldValue("email", initialData.email || "");
      form.setFieldValue("website", initialData.website || "");
      form.setFieldValue("fanpage", initialData.fanpage || "");
      form.setFieldValue("logo", initialData.logo || "");
      form.setFieldValue("address", initialData.address || "");
      form.setFieldValue("deliveryAddress", initialData.deliveryAddress || "");
      form.setFieldValue("contactPerson", initialData.contactPerson || "");
      form.setFieldValue("deliveryMethod", initialData.deliveryMethod || "Giao tới khách (Xe tải APV)");
      form.setFieldValue("transitAddress", initialData.transitAddress || "");
      form.setFieldValue("paymentTerms", initialData.paymentTerms || "Gối đầu 30 ngày");
      form.setFieldValue("colorTolerance", initialData.colorTolerance || "Delta E ≤ 2.0");
      form.setFieldValue("tolerancePercent", Number(initialData.tolerancePercent ?? 1.0));
      form.setFieldValue("bankAccountNumber", initialData.bankAccountNumber || "");
      form.setFieldValue("bankAccountHolder", initialData.bankAccountHolder || "");
      form.setFieldValue("bankName", initialData.bankName || "");
      form.setFieldValue("initialDebt", Number(initialData.initialDebt || 0));
      form.setFieldValue("creditLimit", Number(initialData.creditLimit || 0));
      form.setFieldValue("salesUserId", initialData.salesUserId || null);
      form.setFieldValue("tags", initialData.tags || "Mới");
      form.setFieldValue("notes", initialData.notes || "");
      form.setFieldValue("status", (initialData.status as any) || "ACTIVE");
    }
  }, [initialData, isEdit]);

  // Reactive watches
  const nameValue = useStore(form.store, (s: any) => s.values.name);
  const codeValue = useStore(form.store, (s: any) => s.values.code);
  const shortNameValue = useStore(form.store, (s: any) => s.values.shortName);
  const taxCodeValue = useStore(form.store, (s: any) => s.values.taxCode);
  const phoneValue = useStore(form.store, (s: any) => s.values.phone);
  const representativeValue = useStore(form.store, (s: any) => s.values.representative);
  const deliveryMethodValue = useStore(form.store, (s: any) => s.values.deliveryMethod);
  const logoValue = useStore(form.store, (s: any) => s.values.logo);
  const addressValue = useStore(form.store, (s: any) => s.values.address);

  // Tìm khách hàng trùng trên server theo đúng MST / mã / tên đang nhập (không giới hạn 50 khách hàng đầu)
  const dupKeys = useDebounce(
    {
      taxCode: (taxCodeValue || "").trim(),
      code: (codeValue || "").trim(),
      name: (shortNameValue || nameValue || "").trim(),
    },
    400,
  );
  const { data: existingCustomers = [] } = useQuery({
    queryKey: ["customers-all-check", dupKeys],
    enabled: !!(dupKeys.taxCode || dupKeys.code || dupKeys.name.length > 3),
    queryFn: async () => {
      const calls: Promise<any>[] = [];
      if (dupKeys.taxCode) calls.push(trpcClient.customers.list.query({ taxCode: dupKeys.taxCode, limit: 20 }));
      if (dupKeys.code) calls.push(trpcClient.customers.list.query({ code: dupKeys.code, limit: 20 }));
      if (dupKeys.name.length > 3) calls.push(trpcClient.customers.list.query({ search: dupKeys.name, limit: 20 }));
      const results = await Promise.all(calls);
      const byId = new Map<string, any>();
      results.forEach((r: any) => (r?.items || []).forEach((c: any) => byId.set(c.id, c)));
      return Array.from(byId.values());
    },
  });

  // Hard blocking errors (Chỉ chặn khi tạo mới bị trùng mã hoặc MST)
  const duplicateErrors = useMemo(() => {
    if (!existingCustomers.length) return [];
    return existingCustomers.filter((c: any) => {
      if (isEdit && c.id === customerId) return false;
      if (c.isDeleted) return false;

      // Trùng mã khách hàng với khách hàng đang hoạt động khác
      const cleanCode = (codeValue || "").trim().toUpperCase();
      const cCode = (c.code || "").trim().toUpperCase();
      if (cleanCode && cCode && cleanCode === cCode) return true;

      // Khi Tạo mới: chặn cứng nếu trùng Mã số thuế
      if (!isEdit && taxCodeValue && taxCodeValue.trim() !== "") {
        const cleanTax = taxCodeValue.trim();
        const cTax = (c.taxCode || "").trim();
        if (cleanTax && cTax && cleanTax === cTax) return true;
      }

      return false;
    });
  }, [existingCustomers, codeValue, taxCodeValue, isEdit, customerId]);

  // Soft warnings (Cảnh báo tham khảo, KHÔNG chặn nút lưu)
  const duplicateWarnings = useMemo(() => {
    if (!existingCustomers.length) return [];
    return existingCustomers.filter((c: any) => {
      if (isEdit && c.id === customerId) return false;
      if (c.isDeleted) return false;

      // Khi sửa: cảnh báo nếu trùng MST với khách hàng khác
      if (isEdit && taxCodeValue && taxCodeValue.trim() !== "") {
        const cleanTax = taxCodeValue.trim();
        const cTax = (c.taxCode || "").trim();
        if (cleanTax && cTax && cleanTax === cTax) return true;
      }

      const nInput = normalize(nameValue);
      const nCName = normalize(c.name);
      if (nInput && nInput.length > 5 && nCName === nInput) return true;

      const snInput = normalize(shortNameValue);
      const snCName = normalize(c.shortName || "");
      if (snInput && snInput.length > 3 && snCName === snInput) return true;

      return false;
    });
  }, [existingCustomers, nameValue, shortNameValue, taxCodeValue, isEdit, customerId]);

  // Handler for Name change
  const handleNameChange = (val: string) => {
    form.setFieldValue("name", val);
  };

  // Handler for ShortName change: auto-generate customer code
  const handleShortNameChange = (val: string) => {
    form.setFieldValue("shortName", val);
    if (!isEdit) {
      const currentCode = form.getFieldValue("code") || "";
      if (!currentCode || currentCode === lastAutoCustomerCode.current) {
        const suggested = generateCustomerCode(val);
        form.setFieldValue("code", suggested);
        lastAutoCustomerCode.current = suggested;
      }
    }
  };

  // Handler for Phone change
  const handlePhoneChange = (val: string) => {
    form.setFieldValue("phone", val);
  };

  // Handler for Representative change
  const handleRepresentativeChange = (val: string) => {
    form.setFieldValue("representative", val);
  };

  // Handler for Address change: auto-sync to deliveryAddress if empty
  const handleAddressChange = (val: string) => {
    form.setFieldValue("address", val);
    const currentDelivery = form.getFieldValue("deliveryAddress") || "";
    if (!currentDelivery) {
      form.setFieldValue("deliveryAddress", val);
    }
  };

  // Tra cứu MST qua VietQR
  const handleLookupTax = async () => {
    let cleanTax = (taxCodeValue || "").trim();
    if (!cleanTax) {
      toast.warning("Vui lòng nhập Mã số thuế trước khi tra cứu.");
      return;
    }
    if (cleanTax.length === 13 && !cleanTax.includes("-")) {
      cleanTax = `${cleanTax.slice(0, 10)}-${cleanTax.slice(10)}`;
      form.setFieldValue("taxCode", cleanTax);
    }
    setIsLookingUpTax(true);
    try {
      const info = await lookupBusinessByTaxCode(cleanTax);
      if (info && info.name) {
        form.setFieldValue("name", info.name);
        // Tự động điền tên chủ tài khoản nếu đang trống
        if (!form.getFieldValue("bankAccountHolder")) {
          form.setFieldValue("bankAccountHolder", info.name.toUpperCase());
        }
        if (info.address) {
          form.setFieldValue("address", info.address);
          if (!form.getFieldValue("deliveryAddress")) {
            form.setFieldValue("deliveryAddress", info.address);
          }
        }
        if (info.shortName) {
          handleShortNameChange(info.shortName);
        }
        if (info.status) {
          if (info.status.toLowerCase().includes("ngừng") || info.status.toLowerCase().includes("đóng")) {
            toast.warning(`Trạng thái MST: ${info.status}`);
          } else {
            toast.success(`Đã tự động lấy dữ liệu: ${info.name}`);
          }
        } else {
          toast.success(`Đã tự động lấy dữ liệu: ${info.name}`);
        }
      } else {
        toast.warning("Không tìm thấy thông tin công ty từ mã số thuế này.");
      }
    } catch {
      toast.error("Lỗi khi kết nối cổng tra cứu mã số thuế.");
    } finally {
      setIsLookingUpTax(false);
    }
  };

  // Tải Logo KH
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      validateUploadFile(file, {
        maxSize: MAX_AVATAR_LOGO_SIZE,
        allowedExtensions: ['.png', '.jpg', '.jpeg', '.webp', '.svg'],
        label: 'Ảnh logo khách hàng',
      });
      setIsUploadingLogo(true);
      const res = await uploadFile(file, { preservePng: true });
      form.setFieldValue("logo", res.url);
      toast.success("Đã tải lên logo khách hàng");
    } catch (err: any) {
      toast.error(extractErrorMessage(err, "Lỗi tải ảnh logo"));
    } finally {
      setIsUploadingLogo(false);
      e.target.value = "";
    }
  };

  const isTransitHighlighted =
    deliveryMethodValue?.toLowerCase().includes("chành xe") ||
    deliveryMethodValue?.toLowerCase().includes("nhà xe");

  const emailValue = useStore(form.store, (s: any) => s.values.email);
  // Đủ thông tin xuất HĐ = MST + Tên đầy đủ + Địa chỉ + Email nhận hoá đơn (hợp lệ)
  const invoiceMissing = customerInvoiceMissing({ name: nameValue, taxCode: taxCodeValue, address: addressValue, email: emailValue });
  const isInvoiceReady = invoiceMissing.length === 0;

  if (isEdit && isLoading) {
    return (
      <div className="p-12 text-center text-slate-500 flex flex-col items-center justify-center gap-2">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        <span>Đang tải thông tin khách hàng...</span>
      </div>
    );
  }

    const actions = (
    <div className="flex items-center space-x-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => (props?.onCancel ? props.onCancel() : navigate({ to: "/customers" }))}
        disabled={props?.isSubmitting || createMutation.isPending || updateMutation.isPending}
      >
        Hủy
      </Button>
      <Button
        type="button"
        size="sm"
        className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
        disabled={
          props?.isSubmitting ||
          createMutation.isPending ||
          updateMutation.isPending ||
          duplicateErrors.length > 0
        }
        onClick={() => {
          form.handleSubmit();
          requestAnimationFrame(() => focusFirstError(form.state as any));
        }}
      >
        {createMutation.isPending || updateMutation.isPending ? (
          <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
        ) : (
          <Save className="w-4 h-4 mr-1.5" />
        )}
        {props?.submitLabel || (isEdit ? "Cập nhật" : "Tạo mới")}
      </Button>
    </div>
  );

  const formContent = (
    <div className="space-y-4">{/* ═══ DUPLICATE WARNING ═══ */}
      {duplicateErrors.length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-3.5 flex items-start gap-2.5">
          <AlertTriangle className="h-5 w-5 text-rose-600 mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-semibold text-rose-800">Lỗi: Trùng mã khách hàng hoặc mã số thuế!</p>
            <p className="text-xs text-rose-700 mt-0.5">
              Hệ thống phát hiện mã khách hàng hoặc mã số thuế này đã tồn tại trong CSDL:
            </p>
            <ul className="mt-1.5 space-y-1 text-xs text-rose-800 font-medium">
              {duplicateErrors.map((c: any) => (
                <li key={c.id} className="flex flex-wrap items-center gap-2">
                  <span>
                    • {c.code} — {c.shortName ? `[${c.shortName}] ` : ""}
                    {c.name} {c.taxCode ? `(MST: ${c.taxCode})` : ""}
                  </span>
                  {props?.onUseExisting ? (
                    <Button
                      type="button"
                      size="sm"
                      data-testid="use-existing-customer"
                      className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => props.onUseExisting!(c)}
                    >
                      Dùng khách hàng này
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      data-testid="open-existing-customer"
                      className="h-7"
                      onClick={() => navigate({ to: "/customers/$customerId", params: { customerId: c.id } } as any)}
                    >
                      Mở khách hàng
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {duplicateWarnings.length > 0 && duplicateErrors.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3.5 flex items-start gap-2.5">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-semibold text-amber-800">Lưu ý: Có khách hàng tên hoặc MST tương tự</p>
            <p className="text-xs text-amber-700 mt-0.5">
              Hệ thống tìm thấy khách hàng sau để bạn tham khảo đối chiếu (không chặn việc lưu thông tin):
            </p>
            <ul className="mt-1.5 space-y-1 text-xs text-amber-800 font-medium">
              {duplicateWarnings.map((c: any) => (
                <li key={c.id}>
                  • {c.code} — {c.shortName ? `[${c.shortName}] ` : ""}
                  {c.name} {c.taxCode ? `(MST: ${c.taxCode})` : ""}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* ═══ MAIN FORM CARD ═══ */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-xs p-4 sm:p-5 space-y-5">
        {/* ────────────────────────────────────────────────────────── */}
        {/* PHẦN 1: THÔNG TIN CƠ BẢN (Hero Bar MST + Grid Tên 2 Cột + Logo) */}
        {/* ────────────────────────────────────────────────────────── */}
        <div>
          <h3 className="text-sm font-bold text-emerald-700 border-b border-emerald-200 pb-1.5 mb-3 flex items-center gap-1.5">
            <Building2 className="w-4 h-4 text-emerald-600" /> Thông tin cơ bản
          </h3>

          <div className="flex flex-col lg:flex-row gap-4 items-start">
            {/* CỘT CHÍNH (Chiếm toàn bộ không gian bên trái) */}
            <div className="flex-1 w-full space-y-2.5">
              {/* HERO BAR: Tra cứu Mã số thuế 1-chạm */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5">
                <div className="flex-1 flex items-center gap-2">
                  <div className="text-xs font-bold text-slate-700 whitespace-nowrap flex items-center gap-1">
                    <Search className="w-3.5 h-3.5 text-blue-600" /> Mã số thuế:
                  </div>
                  <form.Field
                    name="taxCode"
                    children={(field) => (
                      <Input
                        className="h-8 text-sm font-mono tracking-wider bg-white flex-1 max-w-xs"
                        placeholder="Nhập 10 hoặc 13 số..."
                        value={field.state.value || ""}
                        onChange={(e) => field.handleChange(e.target.value.trim())}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleLookupTax();
                          }
                        }}
                        onBlur={field.handleBlur}
                      />
                    )}
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleLookupTax}
                    disabled={isLookingUpTax || !taxCodeValue}
                    className="h-8 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-3 shrink-0 flex items-center gap-1.5 cursor-pointer shadow-xs"
                  >
                    {isLookingUpTax ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Đang tra...
                      </>
                    ) : (
                      <>
                        <Search className="w-3.5 h-3.5" /> Tra cứu MST 1-chạm
                      </>
                    )}
                  </Button>
                </div>
                <div className="text-[11px] text-slate-500 italic hidden sm:block">
                  (Tự động điền Tên đầy đủ, Tên viết tắt, Mã KH & Địa chỉ)
                </div>
              </div>

              {/* LƯỚI TRƯỜNG THÔNG TIN: 3 CỘT */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-2">
                {/* Hàng 2: Tên đầy đủ (MERGE 2 CỘT RỘNG RÃI) | Tên viết tắt (1 CỘT) */}
                <div className="sm:col-span-2 md:col-span-2">
                  <form.Field
                    name="name"
                    children={(field) => (
                      <TanstackFieldWrapper
                        field={field as any}
                        label="Tên đầy đủ"
                        required
                      >
                        <TanstackFormControl>
                          <Input
                            className="h-8 text-sm uppercase font-semibold text-slate-800"
                            value={field.state.value}
                            onChange={(e) => handleNameChange(e.target.value)}
                            onBlur={field.handleBlur}
                            placeholder="VD: CÔNG TY TNHH CÔNG NGHIỆP CASA FIORE VIỆT NAM"
                          />
                        </TanstackFormControl>
                      </TanstackFieldWrapper>
                    )}
                  />
                </div>

                <div className="col-span-1">
                  <form.Field
                    name="shortName"
                    children={(field) => (
                      <TanstackFieldWrapper
                        field={field as any}
                        label="Tên viết tắt"
                        required
                      >
                        <TanstackFormControl>
                          <Input
                            className="h-8 text-sm uppercase font-medium"
                            value={field.state.value}
                            onChange={(e) => handleShortNameChange(e.target.value)}
                            onBlur={field.handleBlur}
                            placeholder="VD: CASA FIORE"
                          />
                        </TanstackFormControl>
                      </TanstackFieldWrapper>
                    )}
                  />
                </div>

                {/* Hàng 3: Mã KH (1 Cột) | Điện thoại (1 Cột) | Email (1 Cột) */}
                <form.Field
                  name="code"
                  children={(field) => (
                    <TanstackFieldWrapper
                      field={field as any}
                      label="Mã khách hàng"
                      required
                    >
                      <TanstackFormControl>
                        <Input
                          className="h-8 text-sm font-mono tracking-wider"
                          placeholder="Tự động từ tên viết tắt"
                          value={field.state.value}
                          disabled={isEdit && Boolean(field.state.value)}
                          onChange={(e) =>
                            field.handleChange(
                              e.target.value.toUpperCase().replace(/[^A-Z0-9\-_.]/g, "").slice(0, 20)
                            )
                          }
                          onBlur={field.handleBlur}
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                />

                <form.Field
                  name="phone"
                  children={(field) => (
                    <TanstackFieldWrapper
                      field={field as any}
                      label="Điện thoại"
                      required
                    >
                      <TanstackFormControl>
                        <Input
                          className="h-8 text-sm"
                          placeholder="0901234567 hoặc +84..."
                          value={field.state.value}
                          onChange={(e) => handlePhoneChange(e.target.value)}
                          onBlur={field.handleBlur}
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                />

                <form.Field
                  name="email"
                  children={(field) => (
                    <TanstackFieldWrapper field={field as any} label="Email nhận hoá đơn">
                      <TanstackFormControl>
                        <Input
                          className="h-8 text-sm"
                          type="email"
                          placeholder="info@company.vn"
                          value={field.state.value || ""}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                />

                {/* Hàng 4: Người đại diện (1 Cột) | Chức vụ (1 Cột) | Website (1 Cột) */}
                <form.Field
                  name="representative"
                  children={(field) => (
                    <TanstackFieldWrapper field={field as any} label="Người đại diện">
                      <TanstackFormControl>
                        <Input
                          className="h-8 text-sm"
                          placeholder="VD: Nguyễn Văn A"
                          value={field.state.value || ""}
                          onChange={(e) => handleRepresentativeChange(e.target.value)}
                          onBlur={field.handleBlur}
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                />

                <form.Field
                  name="position"
                  children={(field) => (
                    <TanstackFieldWrapper field={field as any} label="Chức vụ">
                      <TanstackFormControl>
                        <ComboboxConfigMaster
                          categoryCode="CUSTOMER_POSITION"
                          valueField="name"
                          value={field.state.value || "Giám đốc"}
                          onChange={field.handleChange}
                          placeholder="Chọn hoặc thêm chức vụ..."
                          hideLabel
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                />

                <form.Field
                  name="website"
                  children={(field) => (
                    <TanstackFieldWrapper field={field as any} label="Website">
                      <TanstackFormControl>
                        <Input
                          className="h-8 text-sm"
                          placeholder="https://..."
                          value={field.state.value || ""}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                />

                {/* Hàng 5: Địa chỉ trụ sở & Địa chỉ giao hàng (Textarea 2 dòng, merge 2 cột mỗi ô) */}
                <div className="sm:col-span-2 md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-2">
                  <form.Field
                    name="address"
                    children={(field) => (
                      <TanstackFieldWrapper
                        field={field as any}
                        label="Địa chỉ trụ sở (Theo ĐKKD)"
                        required
                      >
                        <TanstackFormControl>
                          <Textarea
                            className="text-xs resize-none min-h-[46px] py-1.5 leading-relaxed"
                            rows={2}
                            placeholder="Số nhà, đường, phường/xã, quận/huyện, tỉnh/thành..."
                            value={field.state.value || ""}
                            onChange={(e) => handleAddressChange(e.target.value)}
                            onBlur={field.handleBlur}
                          />
                        </TanstackFormControl>
                      </TanstackFieldWrapper>
                    )}
                  />

                  <form.Field
                    name="deliveryAddress"
                    children={(field) => (
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs font-semibold text-slate-700">
                            Địa chỉ giao hàng{" "}
                            <span className="text-[10px] text-slate-400 font-normal">
                              (để trống nếu giống địa chỉ chính)
                            </span>
                          </Label>
                          {addressValue && (
                            <button
                              type="button"
                              onClick={() => form.setFieldValue("deliveryAddress", addressValue)}
                              className="text-[10px] text-emerald-600 hover:text-emerald-800 font-medium cursor-pointer flex items-center gap-0.5"
                            >
                              📋 Cùng địa chỉ chính
                            </button>
                          )}
                        </div>
                        <Textarea
                          className="text-xs resize-none min-h-[46px] py-1.5 leading-relaxed"
                          rows={2}
                          placeholder="Cùng địa chỉ..."
                          value={field.state.value || ""}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                        />
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>

            {/* CỘT PHỤ BÊN PHẢI: LOGO KHÁCH HÀNG (Gọn gàng, không dùng float) */}
            <div className="w-full sm:w-32 lg:w-32 shrink-0 flex flex-col items-center gap-1.5 bg-slate-50/70 p-2 rounded-lg border border-slate-200 self-center lg:self-start">
              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-md bg-white border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden relative group">
                {isUploadingLogo ? (
                  <div className="flex flex-col items-center text-xs text-slate-500 gap-1">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                    <span>Đang tải...</span>
                  </div>
                ) : logoValue ? (
                  <img src={logoValue} alt="Logo KH" className="w-full h-full object-contain p-1" />
                ) : (
                  <span className="text-slate-400 text-xs text-center leading-tight">
                    Logo
                    <br />
                    Khách Hàng
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <label
                  htmlFor="logo-upload"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 cursor-pointer transition-colors border border-slate-200"
                >
                  <Camera className="w-3 h-3" /> Tải ảnh
                </label>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                {logoValue && (
                  <button
                    type="button"
                    onClick={() => form.setFieldValue("logo", "")}
                    className="text-[11px] text-rose-500 hover:text-rose-700 p-0.5 flex items-center"
                    title="Xóa logo"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ────────────────────────────────────────────────────────── */}
        {/* PHẦN 2: 2 CỘT SONG SONG CÂN BẰNG (50% - 50%)              */}
        {/* ────────────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 pt-2 border-t border-slate-100">
          {/* CỘT TRÁI: ĐẠI DIỆN & GIAO DỊCH */}
          <div className="space-y-2.5">
            <h3 className="text-sm font-bold text-emerald-700 border-b border-emerald-200 pb-1.5 flex items-center gap-1.5">
              <UserCheck className="w-4 h-4 text-emerald-600" /> Đại diện & Giao dịch
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
              <form.Field
                name="contactPerson"
                children={(field) => (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-semibold text-slate-700">Người liên hệ</Label>
                      {representativeValue && (
                        <button
                          type="button"
                          onClick={() => form.setFieldValue("contactPerson", representativeValue)}
                          className="text-[10px] text-emerald-600 hover:text-emerald-800 font-medium cursor-pointer"
                        >
                          📋 Giống Người đại diện
                        </button>
                      )}
                    </div>
                    <Input
                      className="h-8 text-sm"
                      placeholder="VD: Nguyễn Văn B"
                      value={field.state.value || ""}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </div>
                )}
              />

              <form.Field
                name="mobile"
                children={(field) => (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-semibold text-slate-700">Số điện thoại liên hệ</Label>
                      {phoneValue && (
                        <button
                          type="button"
                          onClick={() => form.setFieldValue("mobile", phoneValue)}
                          className="text-[10px] text-emerald-600 hover:text-emerald-800 font-medium cursor-pointer"
                        >
                          📋 Giống SĐT chính
                        </button>
                      )}
                    </div>
                    <Input
                      className="h-8 text-sm"
                      placeholder="0901..."
                      value={field.state.value || ""}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                    {field.state.meta.errors?.[0] && (
                      <p className="text-[11px] text-rose-600 font-medium leading-tight">
                        {typeof field.state.meta.errors[0] === "string"
                          ? field.state.meta.errors[0]
                          : (field.state.meta.errors[0] as any)?.message}
                      </p>
                    )}
                  </div>
                )}
              />

              <form.Field
                name="paymentTerms"
                children={(field) => (
                  <TanstackFieldWrapper field={field as any} label="Phương thức thanh toán">
                    <TanstackFormControl>
                      <ComboboxConfigMaster
                        categoryCode="PAYMENT_TERMS"
                        valueField="name"
                        value={field.state.value || ""}
                        onChange={field.handleChange}
                        placeholder="Chọn hoặc thêm điều khoản TT..."
                        hideLabel
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                )}
              />

              <form.Field
                name="deliveryMethod"
                children={(field) => (
                  <TanstackFieldWrapper field={field as any} label="Hình thức giao hàng">
                    <TanstackFormControl>
                      <ComboboxConfigMaster
                        categoryCode="DELIVERY_METHODS"
                        valueField="name"
                        value={field.state.value || ""}
                        onChange={(val) => field.handleChange(val)}
                        placeholder="Chọn hoặc thêm hình thức giao..."
                        hideLabel
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                )}
              />

              <div className="col-span-1 sm:col-span-2">
                <form.Field
                  name="transitAddress"
                  children={(field) => (
                    <TanstackFieldWrapper
                      field={field as any}
                      label="Địa chỉ nhà xe / trung chuyển"
                    >
                      <TanstackFormControl>
                        <Input
                          className={`h-8 text-sm transition-colors ${
                            isTransitHighlighted
                              ? "border-amber-400 bg-amber-50/30 focus-visible:ring-amber-400"
                              : ""
                          }`}
                          placeholder="VD: Nhà xe Phương Trang, Q.Bình Thạnh, Bến xe Miền Đông..."
                          value={field.state.value || ""}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                />
              </div>

              <form.Field
                name="colorTolerance"
                children={(field) => (
                  <TanstackFieldWrapper field={field as any} label="Dung sai màu sắc">
                    <TanstackFormControl>
                      <ComboboxConfigMaster
                        categoryCode="COLOR_TOLERANCE"
                        valueField="name"
                        value={field.state.value || ""}
                        onChange={field.handleChange}
                        placeholder="Chọn hoặc thêm dung sai màu..."
                        hideLabel
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                )}
              />

              <form.Field
                name="tolerancePercent"
                children={(field) => (
                  <TanstackFieldWrapper field={field as any} label="Dung sai số lượng">
                    <TanstackFormControl>
                      <NumericInput
                        className="h-8 text-sm text-right"
                        placeholder="VD: ± 10%"
                        suffix="%"
                        min={0}
                        max={10}
                        value={field.state.value}
                        onChange={(val) => field.handleChange(val ?? 1.0)}
                        onBlur={field.handleBlur}
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                )}
              />
            </div>
          </div>

          {/* CỘT PHẢI: NGÂN HÀNG & PHÂN LOẠI */}
          <div className="space-y-2.5">
            <h3 className="text-sm font-bold text-emerald-700 border-b border-emerald-200 pb-1.5 flex items-center gap-1.5">
              <CreditCard className="w-4 h-4 text-emerald-600" /> Ngân hàng & Phân loại
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
              <form.Field
                name="bankAccountNumber"
                children={(field) => (
                  <TanstackFieldWrapper field={field as any} label="Số tài khoản ngân hàng">
                    <TanstackFormControl>
                      <Input
                        className="h-8 text-sm font-mono"
                        placeholder="0123456789..."
                        value={field.state.value || ""}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                )}
              />

              <form.Field
                name="bankAccountHolder"
                children={(field) => (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-semibold text-slate-700">Tên tài khoản</Label>
                      {nameValue && (
                        <button
                          type="button"
                          onClick={() => form.setFieldValue("bankAccountHolder", nameValue.toUpperCase())}
                          className="text-[10px] text-emerald-600 hover:text-emerald-800 font-medium cursor-pointer"
                        >
                          📋 Giống Tên công ty
                        </button>
                      )}
                    </div>
                    <Input
                      className="h-8 text-sm uppercase"
                      placeholder="CONG TY TNHH..."
                      value={field.state.value || ""}
                      onChange={(e) => field.handleChange(e.target.value.toUpperCase())}
                      onBlur={field.handleBlur}
                    />
                  </div>
                )}
              />

              <div className="col-span-1 sm:col-span-2">
                <form.Field
                  name="bankName"
                  children={(field) => (
                    <TanstackFieldWrapper field={field as any} label="Ngân hàng">
                      <TanstackFormControl>
                        <ComboboxConfigMaster
                          categoryCode="bank_names"
                          valueField="name"
                          value={field.state.value || ""}
                          onChange={field.handleChange}
                          placeholder="Chọn hoặc thêm ngân hàng..."
                          hideLabel
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                />
              </div>

              {/* Hạn mức công nợ + Quick Pills */}
              <form.Field
                name="creditLimit"
                children={(field) => (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-semibold text-slate-800">
                        Hạn mức công nợ
                      </Label>
                      <span className="text-[10px] text-slate-400">0 = Không giới hạn</span>
                    </div>
                    <NumericInput
                      name="creditLimit"
                      className="h-8 text-sm text-right font-mono font-semibold text-rose-600"
                      placeholder="VD: 100.000.000"
                      suffix="đ"
                      min={0}
                      value={field.state.value}
                      onChange={(val) => field.handleChange(val ?? 0)}
                      onBlur={field.handleBlur}
                    />
                    {/* Quick Pills */}
                    <div className="flex flex-wrap gap-1 pt-0.5">
                      {QUICK_CREDIT_LIMITS.map((amt) => {
                        const isSelected = Number(field.state.value) === amt.value;
                        return (
                          <button
                            type="button"
                            key={amt.value}
                            onClick={() => field.handleChange(amt.value)}
                            className={`text-[9px] px-1.5 py-0.5 rounded border transition-colors cursor-pointer ${
                              isSelected
                                ? "bg-blue-600 text-white border-blue-600 font-bold"
                                : "bg-slate-50 hover:bg-slate-100 text-slate-600 border-slate-200"
                            }`}
                          >
                            {amt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              />

              <form.Field
                name="type"
                children={(field) => (
                  <TanstackFieldWrapper field={field as any} label="Loại khách hàng">
                    <TanstackFormControl>
                      <Select
                        value={field.state.value || "COMPANY"}
                        onValueChange={(val) => field.handleChange(val)}
                      >
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue placeholder="Chọn loại" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="COMPANY">Doanh nghiệp</SelectItem>
                          <SelectItem value="INDIVIDUAL">Cá nhân</SelectItem>
                        </SelectContent>
                      </Select>
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                )}
              />

              <form.Field
                name="group"
                children={(field) => (
                  <TanstackFieldWrapper field={field as any} label="Nhóm">
                    <TanstackFormControl>
                      <ComboboxConfigMaster
                        categoryCode="customer_groups"
                        valueField="name"
                        value={field.state.value}
                        onChange={field.handleChange}
                        placeholder="Chọn nhóm..."
                        hideLabel
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                )}
              />
            </div>
          </div>
        </div>

        {/* ────────────────────────────────────────────────────────── */}
        {/* PHẦN 3: PHÂN KHÚC, HÓA ĐƠN, QUẢN TRỊ & GHI CHÚ (Footer)   */}
        {/* ────────────────────────────────────────────────────────── */}
        <div className="pt-3 border-t border-slate-100 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 items-center">
            {/* Phân khúc */}
            <form.Field
              name="tags"
              children={(field) => (
                <TanstackFieldWrapper field={field as any} label="Phân khúc">
                  <TanstackFormControl>
                    <ComboboxConfigMaster
                      categoryCode="customer_segments"
                      valueField="name"
                      value={field.state.value || "Mới tiếp cận"}
                      onChange={field.handleChange}
                      placeholder="Chọn hoặc thêm phân khúc..."
                      hideLabel
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />

            {/* Switch Đủ thông tin xuất HĐ */}
            <div className="flex items-center space-x-2 pt-4">
              <Switch id="invoice-ready-switch" checked={isInvoiceReady} disabled />
              <Label
                htmlFor="invoice-ready-switch"
                data-testid="customer-invoice-ready"
                data-ready={isInvoiceReady ? "true" : "false"}
                className={`text-xs font-medium flex items-start gap-1 ${
                  isInvoiceReady ? "text-emerald-700 font-semibold" : "text-amber-700"
                }`}
              >
                {isInvoiceReady ? (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    Đủ thông tin xuất HĐ
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-px" />
                    <span>Thiếu thông tin xuất HĐ: {invoiceMissing.join(", ")}</span>
                  </>
                )}
              </Label>
            </div>

            {/* NV Kinh doanh phụ trách */}
            <div className="sm:col-span-2">
              <form.Field
                name="salesUserId"
                children={(field) => (
                  <TanstackFieldWrapper
                    field={field as any}
                    label="NV Kinh doanh phụ trách"
                  >
                    <TanstackFormControl>
                      <ComboboxFK
                        value={field.state.value || ""}
                        onChange={field.handleChange}
                        options={salesLookup.options}
                        placeholder="Chọn NV Kinh doanh..."
                        loading={salesLookup.isLoading}
                        error={Boolean(field.state.meta.errors.length)}
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                )}
              />
            </div>
          </div>

          {/* Ghi chú */}
          <form.Field
            name="notes"
            children={(field) => (
              <TanstackFieldWrapper field={field as any} label="Ghi chú">
                <TanstackFormControl>
                  <Textarea
                    className="h-16 resize-none text-sm"
                    placeholder="Nhập ghi chú nội bộ về khách hàng..."
                    value={field.state.value || ""}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                  />
                </TanstackFormControl>
              </TanstackFieldWrapper>
            )}
          />
        </div>
      </div>
    </div>
  );

  if (isModal) {
    return (
      <div className="flex flex-col">
        <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-xs pb-2.5 pt-1 mb-3 border-b border-slate-200 flex items-center justify-between gap-4 pr-6">
          <div className="min-w-0 flex-1">
            {props?.modalHeader || (
              <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-600 shrink-0" />
                <span>{isEdit ? "Cập nhật thông tin khách hàng" : "Thêm mới khách hàng"}</span>
              </h3>
            )}
          </div>
          <div className="shrink-0">{actions}</div>
        </div>
        <div className="space-y-4 px-1">
          {formContent}
        </div>
        <div className="sticky bottom-0 z-20 bg-white/95 backdrop-blur-xs flex justify-end space-x-2 py-3 px-2 mt-4 border-t border-slate-200 shadow-[0_-4px_12px_rgba(0,0,0,0.04)]">
          {actions}
        </div>
      </div>
    );
  }

  return (
    <StandardDetailLayout
      title={isEdit ? "Cập nhật khách hàng" : "Thêm mới khách hàng"}
      breadcrumbs={[
        { label: "Khách hàng", to: "/customers" },
        { label: isEdit ? "Cập nhật" : "Thêm mới" },
      ]}
      actions={actions}
    >
      <div className="space-y-4 max-w-5xl mx-auto py-2">
        {formContent}
        <div className="flex justify-end space-x-2 pt-4 border-t border-slate-200">
          {actions}
        </div>
      </div>
    </StandardDetailLayout>
  );
}

export default CustomerForm;
